import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";
import { camelToSnake, snakeToCamel } from "caseparser";

export enum BillingTagType {
  Plan = "Billing.Plan",
  Customer = "Billing.Customer",
  PaymentMethod = "Billing.PaymentMethod",
  PaymentIntent = "Billing.PaymentIntent",
  Invoice = "Billing.Invoice",
  PromotionCode = "Billing.PromotionCode",
  Subscription = "Billing.Subscription",
  SetupIntent = "Billing.SetupIntent",
}

export const billingApi = api
  .enhanceEndpoints({
    addTagTypes: [
      BillingTagType.Plan,
      BillingTagType.Customer,
      BillingTagType.PaymentMethod,
      BillingTagType.PaymentIntent,
      BillingTagType.Invoice,
      BillingTagType.PromotionCode,
      BillingTagType.Subscription,
      BillingTagType.SetupIntent,
    ],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getBillingInvoices: builder.query({
        query: ({ branchId, customerId }) => ({
          url: `branchs/${branchId}/billing/invoices`,
          method: RequestMethod.GET,
          params: camelToSnake({
            customerId,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        providesTags: [BillingTagType.Invoice],
      }),
      createBillingSetupIntent: builder.mutation({
        query: ({ branchId, customerId }) => ({
          url: `branchs/${branchId}/billing/setup-intents`,
          method: RequestMethod.POST,
          body: camelToSnake({
            customerId,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        invalidatesTags: [BillingTagType.SetupIntent],
      }),
      getBillingPaymentMethods: builder.query({
        query: ({ branchId, customerId }) => ({
          url: `branchs/${branchId}/billing/payment-methods`,
          method: RequestMethod.GET,
          params: camelToSnake({
            customerId,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        providesTags: ["Billing.PaymentMethod"],
      }),
      updateBillingPaymentMethod: builder.mutation({
        query: ({ branchId, id, default: _default }) => ({
          url: `branchs/${branchId}/billing/payment-methods/${id}`,
          method: RequestMethod.PATCH,
          body: camelToSnake({
            default: _default,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        invalidatesTags: [BillingTagType.PaymentMethod],
      }),
      deleteBillingPaymentMethod: builder.mutation({
        query: ({ branchId, id }) => ({
          url: `branchs/${branchId}/billing/payment-methods/${id}`,
          method: RequestMethod.DELETE,
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        invalidatesTags: [BillingTagType.PaymentMethod],
      }),
      getBillingPaymentIntents: builder.query({
        query: ({ branchId, customerId }) => ({
          url: `branchs/${branchId}/billing/payment-intents`,
          method: RequestMethod.GET,
          params: camelToSnake({
            customerId,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        providesTags: [BillingTagType.PaymentIntent],
      }),
      getBillingPlans: builder.query({
        query: ({ branchId, search, startingAfter, endingBefore }) => ({
          url: `branchs/${branchId}/billing/plans`,
          method: RequestMethod.GET,
          params: camelToSnake({
            search,
            startingAfter,
            endingBefore,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        providesTags: [BillingTagType.Plan],
      }),
      getBillingPromotionCode: builder.query({
        query: ({ branchId, code }) => ({
          url: `branchs/${branchId}/billing/promotion-codes/${code}`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        providesTags: [BillingTagType.PromotionCode],
      }),
      getBillingSubscription: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/billing/subscriptions`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        providesTags: [BillingTagType.Subscription],
      }),
      createBillingSubscription: builder.mutation({
        query: ({
          branchId,
          paymentMethodId,
          priceId,
          planId,
          customerId,
          code,
          addOns,
        }) => ({
          url: `branchs/${branchId}/billing/subscriptions`,
          method: RequestMethod.POST,
          body: camelToSnake({
            paymentMethodId,
            priceId,
            planId,
            customerId,
            code,
            addOns,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        invalidatesTags: [BillingTagType.Subscription],
      }),
      updateBillingSubscription: builder.mutation({
        query: ({ branchId, status }) => ({
          url: `branchs/${branchId}/billing/subscriptions`,
          method: RequestMethod.PUT,
          body: camelToSnake({
            status,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        invalidatesTags: [BillingTagType.Subscription],
      }),
      createBillingCustomer: builder.mutation({
        query: ({ branchId, data }) => ({
          url: `branchs/${branchId}/billing/customers`,
          method: RequestMethod.POST,
          body: camelToSnake(data),
          transformResponse: (response: any) => snakeToCamel(response),
        }),
        invalidatesTags: [BillingTagType.Customer],
      }),
      updateBillingCustomer: builder.mutation({
        query: ({ branchId, id, name, email, phone, address }) => ({
          url: `branchs/${branchId}/billing/customers/${id}`,
          method: RequestMethod.PUT,
          body: camelToSnake({
            name,
            email,
            phone,
            address,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        invalidatesTags: [BillingTagType.Customer],
      }),
      getBillingCustomer: builder.query({
        query: ({ branchId, id }) => ({
          url: `branchs/${branchId}/billing/customers/${id}`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => snakeToCamel(response),
        providesTags: [BillingTagType.Customer],
      }),

      /**
       * This endpoint doesnt belong here
       */
      assignFeeToUser: builder.mutation({
        query: ({ branchId, data }) => ({
          url: `branchs/${branchId}/fees/assign-fee-to-user`,
          method: RequestMethod.POST,
          body: data,
        }),
        transformResponse: (response: any) => response.data,
      }),
    }),
  });

export const {
  useAssignFeeToUserMutation,
  useGetBillingInvoicesQuery: useGetInvoicesQuery,
  useGetBillingPlansQuery: useGetPlansQuery,
  useGetBillingCustomerQuery: useGetCustomerQuery,
  useGetBillingPaymentMethodsQuery: useGetPaymentMethodsQuery,
  useGetBillingPaymentIntentsQuery: useGetPaymentIntentsQuery,
  useCreateBillingSubscriptionMutation: useCreateSubscriptionMutation,
  useUpdateBillingSubscriptionMutation: useUpdateSubscriptionMutation,
  useCreateBillingCustomerMutation: useCreateCustomerMutation,
  useUpdateBillingCustomerMutation: useUpdateCustomerMutation,
  useCreateBillingSetupIntentMutation: useCreateSetupIntentMutation,
  useDeleteBillingPaymentMethodMutation: useDeletePaymentMethodMutation,
  useUpdateBillingPaymentMethodMutation: useUpdatePaymentMethodMutation,
  useGetBillingPromotionCodeQuery: useGetPromotionCodeQuery,
  useGetBillingSubscriptionQuery: useGetSubscriptionQuery,
} = billingApi;
